import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileSpreadsheet, FileText, Loader2 } from 'lucide-react';
import { DashboardLayout } from '../common/page_component/index.js';
import { useAuth } from '../../context/AuthContext.js';
import reportService from '../../services/ReportService.js';
import {
  buildCleanParams,
  canExportExcel,
  colorMap,
  getDefaultFilters,
  listVisibleReports,
  listVisibleReportsByCategory,
} from './reportConfig.js';

const HubCard = ({ def, onOpen, user }) => {
  const Icon = def.icon;
  const c = colorMap[def.color] || colorMap.indigo;
  const allowExcel = canExportExcel(user);
  const [excelLoading, setExcelLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const download = async (kind) => {
    const setBusy = kind === 'excel' ? setExcelLoading : setPdfLoading;
    setBusy(true);
    try {
      const params = buildCleanParams(getDefaultFilters(def, user));
      if (kind === 'excel') await reportService.downloadExcel(def.type, params);
      else await reportService.downloadPdf(def.type, params);
    } catch (err) {
      alert(err?.response?.data?.error || (kind === 'excel' ? 'Excel download failed' : 'PDF download failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      className="report-card w-full text-left relative overflow-hidden cursor-pointer
        rounded-xl border border-slate-200 bg-white
        shadow-sm transition-transform active:scale-[0.99] touch-manipulation
        flex flex-col"
      style={{ fontFamily: 'inherit' }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = `0 8px 20px ${c.shadow}, 0 1px 4px rgba(15,23,42,0.05)`;
        e.currentTarget.style.transform = 'translateY(-1px)';
        e.currentTarget.style.borderColor = c.buttonBorder;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 1px 2px rgba(15,23,42,0.05)';
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.borderColor = '#e2e8f0';
      }}
    >
      <div
        className="absolute top-0 left-0 right-0 h-0.5 rounded-t-xl pointer-events-none"
        style={{ background: c.topBar }}
      />

      <div className="flex items-start gap-2.5 p-3.5 pb-2.5 flex-1">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          style={{ background: c.iconBg, border: `1px solid ${c.buttonBorder}` }}
        >
          <Icon className="w-4 h-4" style={{ color: c.iconColor }} />
        </div>
        <div className="flex-1 min-w-0 pr-1">
          <h3 className="text-[13px] font-bold text-slate-900 m-0 leading-snug line-clamp-1">
            {def.title}
          </h3>
          <p className="text-[11px] text-slate-400 mt-0.5 m-0 leading-snug line-clamp-2">
            {def.description}
          </p>
        </div>
        <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          {allowExcel && (
            <button
              type="button"
              title="Download Excel"
              aria-label="Download Excel"
              disabled={excelLoading}
              onClick={() => download('excel')}
              className="inline-flex h-9 w-9 items-center justify-center rounded-lg disabled:opacity-60"
              style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857' }}
            >
              {excelLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileSpreadsheet className="w-5 h-5" />}
            </button>
          )}
          <button
            type="button"
            title="Download PDF"
            aria-label="Download PDF"
            disabled={pdfLoading}
            onClick={() => download('pdf')}
            className="inline-flex h-9 w-9 items-center justify-center rounded-lg disabled:opacity-60"
            style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8' }}
          >
            {pdfLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </div>
  );
};

const Reports = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const cards = useMemo(() => listVisibleReports(user), [user]);
  const grouped = useMemo(() => listVisibleReportsByCategory(user), [user]);
  const [activeCategory, setActiveCategory] = useState('all');

  const visibleGroups = useMemo(() => {
    if (activeCategory === 'all') return grouped;
    return grouped.filter((g) => g.id === activeCategory);
  }, [grouped, activeCategory]);

  return (
    <DashboardLayout>
      <div className="w-full max-w-full min-w-0">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center shrink-0">
              <FileSpreadsheet className="w-5 h-5 text-slate-700" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg sm:text-xl font-extrabold text-slate-900 m-0 truncate">
                {t('reports.title')}
              </h1>
              <p className="text-xs text-slate-400 mt-0.5 m-0 font-medium line-clamp-2">
                {t('reports.subtitle', 'Organised by category — open a card for filters, PDF and Excel')}
              </p>
            </div>
          </div>
          <div className="inline-flex self-start sm:self-auto items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-3 py-1.5">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <span className="text-xs font-semibold text-slate-600">
              {cards.length} reports · {grouped.length} categories
            </span>
          </div>
        </div>

        {/* Category filter chips — light blue */}
        <div className="flex flex-wrap items-center gap-1.5 mb-5 pb-3 border-b border-slate-200">
          <button
            type="button"
            onClick={() => setActiveCategory('all')}
            className={`rounded-lg px-2.5 py-1.5 text-[11px] font-semibold border transition-colors ${
              activeCategory === 'all'
                ? 'bg-sky-100 text-sky-800 border-sky-400 shadow-sm'
                : 'bg-sky-50/80 text-sky-700 border-sky-200 hover:bg-sky-100 hover:border-sky-300'
            }`}
          >
            All ({cards.length})
          </button>
          {grouped.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setActiveCategory(cat.id)}
              className={`rounded-lg px-2.5 py-1.5 text-[11px] font-semibold border transition-colors ${
                activeCategory === cat.id
                  ? 'bg-sky-100 text-sky-800 border-sky-400 shadow-sm'
                  : 'bg-sky-50/80 text-sky-700 border-sky-200 hover:bg-sky-100 hover:border-sky-300'
              }`}
            >
              {cat.title} ({cat.reports.length})
            </button>
          ))}
        </div>

        <div className="space-y-7">
          {visibleGroups.map((cat) => (
            <section key={cat.id} className="min-w-0">
              <div className="flex items-end justify-between gap-3 mb-3">
                <div className="min-w-0">
                  <h2 className="text-sm font-extrabold text-slate-900 m-0 tracking-tight">
                    {cat.title}
                  </h2>
                  <p className="text-[11px] text-slate-400 m-0 mt-0.5">
                    {cat.description}
                  </p>
                </div>
                <span className="shrink-0 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  {cat.reports.length} report{cat.reports.length === 1 ? '' : 's'}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {cat.reports.map((def) => (
                  <HubCard
                    key={def.type}
                    def={def}
                    user={user}
                    onOpen={() => navigate(`/reports/${def.path || def.type}`)}
                  />
                ))}
              </div>
            </section>
          ))}

          {visibleGroups.length === 0 && (
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-10 text-center text-sm text-slate-400">
              No reports available for this category.
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Reports;
